import React from "react";
import { Navigate } from "react-router-dom";
import type { RoutesStructure } from "@/types/routestypes";
import Home from "@/pages/Home/Home";
import Chat from "@/pages/Chat/Chat";
import ProfileLayout from "@/pages/Profile/ProfileLayout/ProfileLayout";
import ProfileHome from "@/pages/Profile/Profile/Profile";
import Language from "@/pages/Profile/Languages/Language";
import ProfileSubscription from "@/pages/Profile/Subscription/Subscription";
import BillingHistory from "@/pages/Profile/BillingHistory/BillingHistory";
import ManageMembership from "@/pages/Profile/ManageMembership/ManageMembership";
import ChangeMembership from "@/pages/Profile/ChangeMembership/ChangeMembership";
import HelpCentre from "@/pages/Profile/HelpCentre/HelpCentre";
import {
  PROFILE_BASE_PATH,
  PROFILE_CHILD_PATHS,
} from "@/pages/Profile/profilePaths";
import QuestionnaireManagament from "@/pages/Questionnaire-Management/QuestionnaireManagament";
import Onboarding from "@/pages/Onboarding/Welcome/Onboarding";
import Introduction from "@/pages/Onboarding/Introduction/Introduction";
import PaymentSuccessfull from "@/pages/PaymentSuccessfull/PaymentSuccessfull";
import Waitlist from "@/pages/Waitlist/Waitlist";
import Rejected from "@/pages/Rejected/Rejected";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";
import Event from "@/pages/Events/Event";
import DineWithFounders from "@/pages/DineWithFounders/DineWithFounders";
import HowItWorks from "@/pages/HowItWorks/HowItWorks";
import Membership from "@/pages/Membership/Membership";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";

const GetStarted = React.lazy(
  () => import("@/pages/Auth/GetStarted/GetStarted"),
);
const RoleSelection = React.lazy(
  () => import("@/pages/Auth/RoleSelection/RoleSelection"),
);
const SignUp = React.lazy(() => import("@/pages/Auth/SignUp/SignUp"));
const SignIn = React.lazy(() => import("@/pages/Auth/SignIn/SignIn"));
const ForgotPassword = React.lazy(
  () => import("@/pages/ForgotPassword/ForgotPassword"),
);

const routes = {
  routes: {
    WAITLIST: {
      path: AUTH_ROUTE_PATHS.waitlist,
      element: <Waitlist />,
    },
  },
  publicRoutes: {
    GET_STARTED: {
      path: AUTH_ROUTE_PATHS.getStarted,
      element: <GetStarted />,
      errorElement: <ErrorBoundary />,
    },
    ROLE_SELECTION: {
      path: AUTH_ROUTE_PATHS.roleSelection,
      element: <RoleSelection />,
      errorElement: <ErrorBoundary />,
    },
    SIGN_UP: {
      path: AUTH_ROUTE_PATHS.signUp,
      element: <SignUp />,
      errorElement: <ErrorBoundary />,
    },
    SIGN_IN: {
      path: AUTH_ROUTE_PATHS.signIn,
      element: <SignIn />,
      errorElement: <ErrorBoundary />,
    },
    REJECTED: {
      path: AUTH_ROUTE_PATHS.rejected,
      element: <Rejected />,
      errorElement: <ErrorBoundary />,
    },
    LEGACY_LOGIN: {
      path: AUTH_ROUTE_PATHS.legacyLogin,
      element: <Navigate replace to={AUTH_ROUTE_PATHS.getStarted} />,
      errorElement: <ErrorBoundary />,
    },
    RESET_PASSWORD: {
      path: AUTH_ROUTE_PATHS.resetPassword,
      element: <ForgotPassword />,
      errorElement: <ErrorBoundary />,
    },
    FORGOT_PASSWORD: {
      path: AUTH_ROUTE_PATHS.forgotPassword,
      element: <ForgotPassword />,
      errorElement: <ErrorBoundary />,
    },
    PAYMENT_SUCCESSFUL: {
      path: AUTH_ROUTE_PATHS.paymentSuccessful,
      element: <PaymentSuccessfull />,
      errorElement: <ErrorBoundary />,
    },
  },
  privateRoutes: {
    WELCOME: {
      path: AUTH_ROUTE_PATHS.welcome,
      element: <Onboarding />,
    },
    QUESTIONS: {
      path: AUTH_ROUTE_PATHS.questions,
      element: <QuestionnaireManagament />,
    },
    INTRODUCTION: {
      path: AUTH_ROUTE_PATHS.introduction,
      element: <Introduction />,
    },
    HOME: {
      path: "/home",
      element: <Home />,
      pageName: "Home",
    },
    HOW_IT_WORKS: {
      path: "/how-it-works",
      element: <HowItWorks />,
      pageName: "How BFC Works",
    },
    MEMBERSHIP: {
      path: "/membership",
      element: <Membership />,
      pageName: "Choose Membership",
    },
    DINE_WITH_FOUNDERS: {
      path: "/dine-with-founders",
      element: <DineWithFounders />,
      pageName: "Dine with Founders",
    },
    CHAT: {
      path: "/chat",
      element: <Chat />,
      pageName: "Chat",
    },
    EVENT: {
      path: "/event",
      element: <Event />,
      pageName: "Event",
    },
    PAYMENT_SUCCESSFUL: {
      path: "/payment-successful",
      element: <PaymentSuccessfull />,
      pageName: "Payment Successful",
    },
    PROFILE: {
      path: PROFILE_BASE_PATH,
      element: <ProfileLayout />,
      pageName: "Profile",
      children: {
        HOME: {
          path: PROFILE_CHILD_PATHS.HOME,
          element: <ProfileHome />,
          pageName: "Profile",
        },
        LANGUAGE: {
          path: PROFILE_CHILD_PATHS.LANGUAGE,
          element: <Language />,
          pageName: "Language",
        },
        SUBSCRIPTION: {
          path: PROFILE_CHILD_PATHS.SUBSCRIPTION,
          element: <ProfileSubscription />,
          pageName: "My Subscription",
        },
        BILLING_HISTORY: {
          path: PROFILE_CHILD_PATHS.BILLING_HISTORY,
          element: <BillingHistory />,
          pageName: "Billing History",
        },
        MANAGE_MEMBERSHIP: {
          path: PROFILE_CHILD_PATHS.MANAGE_MEMBERSHIP,
          element: <ManageMembership />,
          pageName: "Manage Membership",
        },
        CHANGE_MEMBERSHIP: {
          path: PROFILE_CHILD_PATHS.CHANGE_MEMBERSHIP,
          element: <ChangeMembership />,
          pageName: "Change Membership",
        },
        HELP_CENTRE: {
          path: PROFILE_CHILD_PATHS.HELP_CENTRE,
          element: <HelpCentre />,
          pageName: "Help Centre & FAQ",
        },
      },
    },
  },
} as const satisfies RoutesStructure;

export default routes;
